import React from 'react';
import { Copy, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Two-state invite dialog: form for creating an invite, or success view with
 * the generated code + share link. Fully controlled by parent state.
 */
const InviteDialog = ({
  open,
  onClose,
  newInvite,
  setNewInvite,
  createdInvite,
  saving,
  onSubmit,
  onCopyCode,
  onCopyLink,
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md border border-zinc-200  rounded-none p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-2xl font-heading font-bold uppercase tracking-wide">
            {createdInvite ? 'Invite Created!' : 'Invite Team Member'}
          </DialogTitle>
        </DialogHeader>

        {createdInvite ? (
          <div className="p-6 pt-4 space-y-4">
            <p className="text-zinc-500">
              Share this invite with <span className="font-bold">{createdInvite.email}</span>
            </p>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">Invite Code</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={createdInvite.invite_code}
                  readOnly
                  className="flex-1 h-14 border border-zinc-200 bg-zinc-50 text-zinc-900 text-lg p-4 font-mono text-center"
                />
                <button
                  onClick={() => onCopyCode(createdInvite.invite_code)}
                  className="h-14 px-4 bg-primary border border-zinc-200  hover:bg-primary-hover"
                >
                  <Copy className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">Or Copy Link</label>
              <button
                onClick={() => onCopyLink(createdInvite.invite_code)}
                className="w-full h-14 bg-white border border-zinc-200 font-bold uppercase hover:bg-zinc-50 flex items-center justify-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copy Invite Link
              </button>
            </div>

            <p className="text-sm text-zinc-500">
              The invite will expire in 7 days. The user will join as <span className="font-bold">{createdInvite.role}</span>.
            </p>

            <button
              onClick={onClose}
              className="w-full h-14 bg-primary text-zinc-900 font-bold uppercase border border-zinc-200  hover:bg-primary-hover active:translate-y-1"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="p-6 pt-4 space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">Email Address</label>
              <input
                type="email"
                value={newInvite.email}
                onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                required
                data-testid="invite-email"
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3"
                placeholder="newteammate@company.com"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">Role</label>
              <select
                value={newInvite.role}
                onChange={(e) => setNewInvite({ ...newInvite, role: e.target.value })}
                data-testid="invite-role"
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3"
              >
                <option value="Project Manager">Project Manager</option>
                <option value="Foreman">Foreman</option>
              </select>
              <p className="text-xs text-zinc-500 mt-2">
                {newInvite.role === 'Project Manager'
                  ? 'Can create/edit projects, phases, orders. Can invite team members.'
                  : 'Can view assigned projects, create orders, and update phase status.'}
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-14 bg-white text-zinc-900 font-bold uppercase border border-zinc-200 hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="send-invite-btn"
                className="flex-1 h-14 bg-primary text-zinc-900 font-bold uppercase border border-zinc-200  hover:bg-primary-hover active:translate-y-1 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Invite'}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InviteDialog;
